import React, { useEffect, useMemo, useState, useCallback, useDeferredValue } from 'react'
import { Row, Col, Spinner, Container } from 'react-bootstrap'
import useFetch from '../../../_fetch'
import { ClickDataTable } from './click-table'
import { formatNumberLocale, formatNumber, toNumber, formatLocalDateForInput, getLocalDateStart, getLocalDateEnd } from "../../../_utils"
import { useAuth } from '../../../auth/auth-context'
import { ClicksPerCampaign,ClicksPerLink,ClicksPerPath,IPublisher,Link,Infos,ITransaction, ImpressionsPerPath } from './types'
import LinkKpiOverview from './link-kpi-overview'
import { buildPageClickReportRows } from './page-click-report-utils'
import DateRangeSelector from '../home/dashboard-content/date-range-selector'
import { loadPref, resolveLabel } from '../home/dashboard-content/date-range-preference'
/*
RPM
Page views
Total clicks
*/
const getDrillLevel = (prefix: string | null): number => {
    if (!prefix) return 1
    const firstSlash = prefix.indexOf('/')
    if (firstSlash === -1) return 1
    const pathPart = prefix.slice(firstSlash)
    return pathPart.split('/').filter(Boolean).length + 1
}

const buildBreadcrumbs = (prefix: string | null): { label: string; prefix: string | null }[] => {
    const crumbs: { label: string; prefix: string | null }[] = [{ label: 'Overview', prefix: null }]
    if (!prefix) return crumbs
    const firstSlash = prefix.indexOf('/')
    if (firstSlash === -1) return crumbs
    const domain = prefix.slice(0, firstSlash)
    const pathPart = prefix.slice(firstSlash)
    const segments = pathPart.split('/').filter(Boolean)
    let pathSoFar = ''
    for (const seg of segments) {
        pathSoFar += '/' + seg
        crumbs.push({ label: '/' + seg + '/', prefix: domain + pathSoFar + '/' })
    }
    return crumbs
}

const LinkScriptDashboard: React.FC<{publishers: IPublisher[]}> = ({ publishers }) => {
    const { doFetch } = useFetch()
    const { user } = useAuth()

    const [infos, setInfos] = useState<Infos & { linkObj: {[key: string]: Link} }>({ domains: [], paths: [], links: [], linkObj: {} })

    const [clicksPerLink, setClicksPerLink] = useState<ClicksPerLink[]>([])
    const [clicksPerPath, setClicksPerPath] = useState<ClicksPerPath[]>([])
    const [clicksPerCampaign, setClicksPerCampaign] = useState<ClicksPerCampaign[]>([])

    const [badClicksPerLink, setBadClicksPerLink] = useState<ClicksPerLink[]>([])
    const [badClicksPerPath, setBadClicksPerPath] = useState<ClicksPerPath[]>([])

    const [impressionsPerPath, setImpressionsPerPath] = useState<ImpressionsPerPath[]>([])

    const [rawTransactions, setRawTransactions] = useState<any[]>([])

    const [subPathDrillPrefix, setSubPathDrillPrefix] = useState<string | null>(null)
    const deferredDrillPrefix = useDeferredValue(subPathDrillPrefix)
    const isSubPathTransitioning = deferredDrillPrefix !== subPathDrillPrefix
    const subPathDrillLevel = useMemo(() => getDrillLevel(subPathDrillPrefix), [subPathDrillPrefix])
    

    const formatTransactions = useCallback((transactions: {ref:string, totalTransactions: number, totalCommission: number}[]) => {
        const formatted:ITransaction[] = []
        const regex = /^n(\d+)n(\d+)n(\d+)n([A-Za-z0-9]+)$/

        for(const transaction of transactions) {
            const match = (transaction.ref || '').match(regex)

            if(!match) continue

            const [, publisherInternalIdStr, domainIdStr, pathIdStr, urlHash] = match
    
            const publisherInternalId = toNumber(publisherInternalIdStr)
            const pathId = toNumber(pathIdStr)
            const domainId = toNumber(domainIdStr)
            const urlId = infos.linkObj[urlHash]?.id
    
            if(!publisherInternalId || !domainId || !pathId || !urlId) continue
    
            formatted.push({
                publisherInternalId,
                domainId,
                pathId,
                urlId,
                totalTransactions: transaction.totalTransactions,
                totalCommission: transaction.totalCommission
            })
        }
        return formatted
    }, [infos.linkObj])

    const transactions = useMemo(() => {
        return formatTransactions(rawTransactions)
    }, [rawTransactions, formatTransactions])

    const [loading, setLoading] = useState(true)

    const [fromDate, setFromDate] = useState(() => {
        const saved = resolveLabel(loadPref('link-script'))
        if (saved) return formatLocalDateForInput(saved.startDate)
        const fromD = new Date()
        fromD.setDate(fromD.getDate() - 30)
        return formatLocalDateForInput(fromD)
    })

    const [toDate, setToDate] = useState(() => {
        const saved = resolveLabel(loadPref('link-script'))
        if (saved) return formatLocalDateForInput(saved.endDate)
        return formatLocalDateForInput(new Date())
    })

    const fetchClicks = async (from?: string, to?: string) => {
        setLoading(true)
        try {
            const finalFrom = getLocalDateStart(from || fromDate)
            const finalTo = getLocalDateEnd(to || toDate)

            const resClicks = await doFetch('/byelink/aggregated-clicks', {
                fromDate: finalFrom.toISOString(),
                toDate: finalTo.toISOString()
            })

            if (resClicks.status >= 400) {
                throw await resClicks.text()
            }

            const clicksJson = await resClicks.json()

            setRawTransactions(clicksJson.transactions)
            // Tracked
            setClicksPerLink(clicksJson.clicks.clicksPerLink)
            setClicksPerPath(clicksJson.clicks.clicksPerPath)
            setClicksPerCampaign(clicksJson.clicks.clicksPerCampaign)

            // Untracked (bad)
            setBadClicksPerLink(clicksJson.badClicks.clicksPerLink)
            setBadClicksPerPath(clicksJson.badClicks.clicksPerPath)

            setImpressionsPerPath(clicksJson.impressions)

        } catch (err) {
            console.error('Error fetching clicks:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const resInfos = await doFetch('/byelink/infos')
                if (resInfos.status >= 400) throw await resInfos.text()
                
                const infosRaw:Infos = await resInfos.json()
                const linkObj: {[key: string]: Link} = {}
                infosRaw.links.forEach(link => {
                    linkObj[link.hash] = link
                })
                setInfos({domains: infosRaw.domains, paths: infosRaw.paths, links: infosRaw.links, linkObj: linkObj})

                await fetchClicks()
            } catch (err) {
                console.error('Error fetching initial data:', err)
                setLoading(false)
            }
        }
        fetchData()
    }, [])

    const filteredPathOverview = useMemo(() => {
        return buildPageClickReportRows({
            domains: infos.domains,
            paths: infos.paths,
            clicksPerPath,
            badClicksPerPath,
            impressionsPerPath,
            transactions: transactions.map(transaction => ({
                pathId: transaction.pathId,
                totalTransactions: transaction.totalTransactions,
                totalCommission: transaction.totalCommission
            }))
        })
    }, [badClicksPerPath, clicksPerPath, impressionsPerPath, infos.domains, infos.paths, transactions])

    const domainOverview = infos.domains.map(domain => {
        const pathsOfDomain = infos.paths.filter(p => p.domain_id === domain.id)

        const tracked = pathsOfDomain.reduce(
            (sum, p) => sum + (clicksPerPath.find(c => c.path_id === p.id)?.clicks || 0),
            0
        )

        const untracked = pathsOfDomain.reduce(
            (sum, p) => sum + (badClicksPerPath.find(c => c.path_id === p.id)?.clicks || 0),
            0
        )

        const domainTransactions = transactions
            .filter(t => pathsOfDomain.some(p => p.id === t.pathId))
            .reduce(
                (acc, t) => {
                    acc.totalTransactions += t.totalTransactions
                    acc.totalCommission += t.totalCommission
                    return acc
                },
                { totalTransactions: 0, totalCommission: 0 }
            )

        const impressions = pathsOfDomain.reduce(
            (sum, p) => sum + (impressionsPerPath.find(i => i.path_id === p.id)?.impressions || 0),
            0
        )

        return {
            domain: domain.domain,
            tracked,
            untracked,
            total: tracked + untracked,
            totalTransactions: domainTransactions.totalTransactions,
            totalCommission: domainTransactions.totalCommission,
            impressions
        }
    })
    const filteredDomainOverview = domainOverview.filter(d => d.total > 0 || d.tracked > 0 || d.impressions > 0)

    const subPathOverview = useMemo(() => {
        const level = getDrillLevel(deferredDrillPrefix)
        if (level > 4) return []

        const groupedRows = new Map<string, {
            page: string
            tracked: number
            untracked: number
            total: number
            totalTransactions: number
            totalCommission: number
            impressions: number
        }>()

        for (const row of filteredPathOverview) {
            const fullKey = `${row.domain}${row.path}`
            if (deferredDrillPrefix && !fullKey.startsWith(deferredDrillPrefix)) continue

            const pathSegments = row.path.split('/').filter(Boolean)
            if (pathSegments.length < level) continue

            const groupedPath = '/' + pathSegments.slice(0, level).join('/') + '/'
            const page = `${row.domain}${groupedPath}`

            const existing = groupedRows.get(page) || {
                page, tracked: 0, untracked: 0, total: 0,
                totalTransactions: 0, totalCommission: 0, impressions: 0
            }
            existing.tracked += row.tracked
            existing.untracked += row.untracked
            existing.total += row.total
            existing.totalTransactions += row.totalTransactions
            existing.totalCommission += row.totalCommission
            existing.impressions += row.impressions
            groupedRows.set(page, existing)
        }

        return Array.from(groupedRows.values())
    }, [filteredPathOverview, deferredDrillPrefix])

    const linkOverviewRows = useMemo(() => {
        const linksById = new Map(infos.links.map(link => [link.id, link]))
        const transactionsByUrlId = new Map<number, { totalTransactions: number; totalCommission: number }>()

        for (const transaction of transactions) {
            const current = transactionsByUrlId.get(transaction.urlId) || { totalTransactions: 0, totalCommission: 0 }
            current.totalTransactions += transaction.totalTransactions
            current.totalCommission += transaction.totalCommission
            transactionsByUrlId.set(transaction.urlId, current)
        }

        return [
            ...clicksPerLink.map(c => {
                const link = linksById.get(c.link_id)
                const tx = transactionsByUrlId.get(c.link_id) || { totalTransactions: 0, totalCommission: 0 }

                return {
                    url: link?.target_url || 'Unknown',
                    NPClicks: c.clicks || 0,
                    Clicks: 0,
                    totalTransactions: tx.totalTransactions,
                    totalCommission: tx.totalCommission
                }
            }),
            ...badClicksPerLink.map(c => {
                const link = linksById.get(c.link_id)
                return {
                    url: link?.target_url || 'Unknown',
                    NPClicks: 0,
                    Clicks: c.clicks || 0,
                    totalTransactions: 0,
                    totalCommission: 0
                }
            })
        ]
    }, [infos.links, transactions, clicksPerLink, badClicksPerLink])


    return (
        <Container fluid>
            <div className="link-script-header mb-4">
                <div>
                    <h3 className="mb-0">Link automation</h3>
                </div>
                <div className="link-script-date-selector">
                    <DateRangeSelector
                        pageKey="link-script"
                        onSubmit={async (startDate, endDate) => {
                            const nextFromDate = formatLocalDateForInput(startDate)
                            const nextToDate = formatLocalDateForInput(endDate)

                            setFromDate(nextFromDate)
                            setToDate(nextToDate)
                            await fetchClicks(nextFromDate, nextToDate)
                        }}
                    />
                </div>
            </div>

            <hr />

            {loading && clicksPerPath.length === 0 ? (
                <Row>
                    <Col className="text-center">
                        <Spinner animation="border" />
                    </Col>
                </Row>
            ) : (
                <>
                        <LinkKpiOverview
                            clicksPerPath={clicksPerPath}
                            badClicksPerPath={badClicksPerPath}
                            impressionsPerPath={impressionsPerPath}
                            transactions={transactions}
                            currency={user?.settings.currency || ''}
                        />
                    <Row>

                        <Col xs={12}>
                            <ClickDataTable
                                title="Page click report"
                                data={filteredPathOverview}
                                    columns={[
                                        { name: 'Page', selector: row => row.domain + row.path, sortable: true, width: '40%', wrap: true },
                                        { name: 'Page views', id: 'PageViews', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true, width: '120px' },
                                        { name: 'CTR', selector: row => row.impressions > 0 ? row.total / row.impressions : 0, format: row => `${formatNumberLocale(row.impressions > 0 ? (row.total / row.impressions) * 100 : 0, 1)}%`, sortable: true },
                                        { name: 'Automated Clicks', selector: row => row.tracked, format: row => formatNumberLocale(row.tracked, 0), sortable: true, id: 'NPClicks', width: '150px' },
                                        { name: 'Total Clicks', selector: row => row.total, format: row => formatNumberLocale(row.total, 0), sortable: true, id: 'Total', width: '120px' },
                                        { name: 'CR', selector: row => row.total > 0 ? row.totalTransactions / row.total : 0, format: row => `${formatNumberLocale(row.total > 0 ? (row.totalTransactions / row.total) * 100 : 0, 2)}%`, sortable: true },
                                        { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                        { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true, width:'120px' },
                                        { name: 'EPC', selector: row => row.total > 0 ? row.totalCommission / row.total : 0, format: row => formatNumber(row.total > 0 ? row.totalCommission / row.total : 0, user?.settings.currency || ''), sortable: true },
                                        { name: 'EPV', selector: row => row.impressions > 0 ? row.totalCommission / row.impressions : 0, format: row => formatNumber(row.impressions > 0 ? row.totalCommission / row.impressions : 0,user?.settings.currency || ''), sortable: true, width: '120px'},
                                    ]}
                                id='PageViews'
                            />
                        </Col>

                       <Col xs={12}>
                            <ClickDataTable
                                title="Link click report"
                                data={linkOverviewRows}
                                paginationPerPage={25}
                                paginationRowsPerPageOptions={[25, 50, 100, 200]}
                                columns={[
                                    { name: 'Target URL', selector: row => row.url, sortable: true, wrap:true, width:'40%' },
                                    { name: 'Automated Clicks', selector: row => row.NPClicks, format: row => formatNumberLocale(row.NPClicks, 0), sortable: true, id: 'NPClicks', width: '150px' },
                                    { 
                                        name: 'Total Clicks', 
                                        selector: row => (row.NPClicks || 0) + (row.Clicks || 0), 
                                        format: row => formatNumberLocale((row.NPClicks || 0) + (row.Clicks || 0), 0), 
                                        sortable: true 
                                    },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => row.totalCommission / (row.NPClicks || 0), format: row => formatNumber((row.totalCommission / (row.NPClicks || 0)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='NPClicks'
                            />
                        </Col>



                        <Col xs={12}>
                            <ClickDataTable
                                title="Campaign overview"
                                data={clicksPerCampaign.map(c => {
                                    const publisher = publishers.find(p => p.internalId === c.campaign_id)
                                    const campaignTransactions = transactions
                                        .filter(t => t.publisherInternalId === c.campaign_id)
                                        .reduce(
                                            (acc, t) => {
                                                acc.totalTransactions += t.totalTransactions
                                                acc.totalCommission += t.totalCommission
                                                return acc
                                            },
                                            { totalTransactions: 0, totalCommission: 0 }
                                        )
                                    return {
                                        name: publisher?.name || 'Unknown',
                                        network: publisher?.network || 'Unknown',
                                        publisherName: publisher?.publisherName || 'Unknown',
                                        clicks: c.clicks,
                                        totalTransactions: campaignTransactions.totalTransactions,
                                        totalCommission: campaignTransactions.totalCommission
                                    }
                                })}
                                columns={[
                                    { name: 'Name', selector: row => row.name, sortable: true },
                                    { name: 'Network', selector: row => row.network, sortable: true },
                                    { name: 'Publisher', selector: row => row.publisherName, sortable: true },
                                    { name: 'Automated Clicks', selector: row => row.clicks, format: row => formatNumberLocale(row.clicks, 0), sortable: true, id: 'Clicks', width: '150px' },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => row.totalCommission / (row.clicks || 0), format: row => formatNumber((row.totalCommission / (row.clicks || 0)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='Clicks'
                            />
                        </Col>

                        <Col xs={12}>
                            {subPathDrillPrefix && (() => {
                                const crumbs = buildBreadcrumbs(subPathDrillPrefix)
                                return (
                                    <div className="d-flex align-items-center gap-1 small px-1 mb-1">
                                        {crumbs.map((crumb, i) => {
                                            const isLast = i === crumbs.length - 1
                                            return (
                                                <React.Fragment key={i}>
                                                    {i > 0 && <span className="text-muted">/</span>}
                                                    {isLast ? (
                                                        <span className="text-dark fw-semibold">{crumb.label}</span>
                                                    ) : (
                                                        <button
                                                            className="btn btn-link btn-sm p-0 text-muted text-decoration-none"
                                                            onClick={() => setSubPathDrillPrefix(crumb.prefix)}
                                                        >
                                                            {crumb.label}
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })}
                                    </div>
                                )
                            })()}
                            <div style={{ opacity: isSubPathTransitioning ? 0.5 : 1, transition: 'opacity 0.15s' }}>
                            <ClickDataTable
                                title="Sub-path overview"
                                data={subPathOverview}
                                columns={[
                                    { name: 'Page', selector: row => row.page, sortable: true, width: '40%', wrap: true },
                                    { name: 'Page views', id: 'PageViews', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true, width: '120px' },
                                    { name: 'CTR', selector: row => row.impressions > 0 ? row.total / row.impressions : 0, format: row => `${formatNumberLocale(row.impressions > 0 ? (row.total / row.impressions) * 100 : 0, 1)}%`, sortable: true },
                                    { name: 'Automated Clicks', selector: row => row.tracked, format: row => formatNumberLocale(row.tracked, 0), sortable: true, id: 'NPClicks', width: '150px' },
                                    { name: 'Total Clicks', selector: row => row.total, format: row => formatNumberLocale(row.total, 0), sortable: true, id: 'Total', width: '120px' },
                                    { name: 'CR', selector: row => row.total > 0 ? row.totalTransactions / row.total : 0, format: row => `${formatNumberLocale(row.total > 0 ? (row.totalTransactions / row.total) * 100 : 0, 2)}%`, sortable: true },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true, width:'120px' },
                                    { name: 'EPC', selector: row => row.total > 0 ? row.totalCommission / row.total : 0, format: row => formatNumber(row.total > 0 ? row.totalCommission / row.total : 0, user?.settings.currency || ''), sortable: true },
                                    { name: 'EPV', selector: row => row.impressions > 0 ? row.totalCommission / row.impressions : 0, format: row => formatNumber(row.impressions > 0 ? row.totalCommission / row.impressions : 0,user?.settings.currency || ''), sortable: true, width: '120px'},
                                ]}
                                id='PageViews'
                                onRowClicked={subPathDrillLevel < 4
                                    ? (row) => setSubPathDrillPrefix(row.page)
                                    : undefined}
                                pointerOnHover={subPathDrillLevel < 4}
                            />
                            </div>
                        </Col>

                        <Col xs={12}>
                            <ClickDataTable
                                title="Domain overview"
                                data={filteredDomainOverview.map(d => ({
                                    domain: d.domain,
                                    NPClicks: d.tracked || 0,      // Mindst 0
                                    Clicks: d.untracked || 0,      // Mindst 0
                                    totalTransactions: d.totalTransactions || 0,
                                    totalCommission: d.totalCommission || 0,
                                    impressions: d.impressions || 0

                                }))}
                                columns={[
                                    { name: 'Domain', selector: row => row.domain, sortable: true },
                                    { name: 'Page views', id: 'PageViews', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true },
                                    { name: 'CTR', selector: row => row.impressions > 0 ? row.Clicks / row.impressions : 0, format: row => `${formatNumberLocale(row.impressions > 0 ? (row.Clicks / row.impressions) * 100 : 0, 1)}%`, sortable: true },
                                    { name: 'Automated Clicks', id: 'NPClicks', selector: row => row.NPClicks, format: row => formatNumberLocale(row.NPClicks, 0), sortable: true, width: '150px' },
                                    { 
                                        name: 'Total Clicks', 
                                        selector: row => (row.NPClicks || 0) + (row.Clicks || 0), 
                                        format: row => formatNumberLocale((row.NPClicks || 0) + (row.Clicks || 0), 0), 
                                        sortable: true, 
                                        id:'Total'
                                    },
                                    { name: 'CR', selector: row => row.Clicks > 0 ? row.totalTransactions / row.Clicks : 0, format: row => `${formatNumberLocale(row.Clicks > 0 ? (row.totalTransactions / row.Clicks) * 100 : 0, 2)}%`, sortable: true },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => row.totalCommission / (row.NPClicks || 0), format: row => formatNumber((row.totalCommission / (row.NPClicks || 0)), user?.settings.currency || ''), sortable: true},
                                    {name: 'EPV', selector: row => row.totalCommission / (row.impressions || 0), format: row => formatNumber((row.totalCommission / (row.impressions || 0)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='PageViews'
                                height="200px"
                            />
                        </Col>

                    </Row>
                </>
            )}
        </Container>
    )
}

export default LinkScriptDashboard
